import { useNoSleep } from '@/hooks/NoSleep';
import { useSetupPlayer } from '@/hooks/PlayerSessionControlHooks';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { ChangeEvent, KeyboardEvent, ReactNode, useState } from 'react';
import AnimatedPlayerSetup from '../Animated/Backgrounds/AnimatedPlayerSetupBackground';
import FeelingMeter from '../Controls/FeelingMeter';
import OldLogoIcon from '../Icons/OldLogoIcon';

export default function PlayerSetup() {
  // States
  const [username, setUsername] = useState<string>('');
  const [sentiment, setSentiment] = useState<number | undefined>(undefined);
  const [wasSubmissionTriggered, setWasSubmissionTriggered] = useState<boolean>(false);
  const [limitError, setLimitError] = useState<boolean>(false);
  const [emptyInput, setEmptyInput] = useState<boolean>(false);
  const [invalidInput, setInvalidInput] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState<boolean>(false);

  // Hooks
  const setupPlayer = useSetupPlayer();
  const { enableNoSleep } = useNoSleep();

  const isInputEmpty = username.trim() === '';

  const inputValidate = () => {
    const regex = /^[a-zA-Z0-9\s\-áàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ]*$/;

    return regex.test(username);
  };

  const inputUsername = (e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    setLimitError(false);
    setEmptyInput(false);
    setInvalidInput(false);
  };

  const resetSubmitButtonStatus = () => {
    setIsSubmitButtonDisabled(false);
    setLoading(false);
  };

  const errorMessage = (): ReactNode => {
    let errorMessage;

    switch (true) {
      case limitError:
        errorMessage = 'Limit 25 characters';
        break;
      case emptyInput:
        errorMessage = '*This field is required';
        break;
      case invalidInput:
        errorMessage = 'Name cannot contain special characters';
        break;
      default:
        errorMessage = null;
        break;
    }

    return errorMessage;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Go') {
      submit();
    }
  };

  const submit = async () => {
    enableNoSleep();
    setWasSubmissionTriggered(true);
    setIsSubmitButtonDisabled(true);
    setLoading(true);

    if (username.length > 25) {
      setLimitError(true);
      resetSubmitButtonStatus();
      return;
    } else if (!inputValidate()) {
      setInvalidInput(true);
      resetSubmitButtonStatus();
      return;
    } else if (isInputEmpty) {
      setEmptyInput(true);
      resetSubmitButtonStatus();
      return;
    }

    if (!sentiment) {
      resetSubmitButtonStatus();
      return;
    }

    setLimitError(false);
    setInvalidInput(false);
    setEmptyInput(false);

    await setupPlayer({
      name: username,
      feelingBefore: sentiment
    });

    resetSubmitButtonStatus();
  };

  return (
    <div className="h-full w-full bg-cover bg-center bg-no-repeat">
      <AnimatedPlayerSetup />
      <div className="flex flex-col justify-between items-center h-full">
        <OldLogoIcon className="my-40 max-w-176 items-center justify-center" />
        <div className="flex flex-col justify-center items-center h-[calc(100%-116px)] max-w-296">
          <span className="font-extrabold text-5xl text-[#1D1A34] mb-32">Welcome!</span>
          <input
            // We set the name attribute to "first_name", so that browsers can autofill the input
            // with the user's first name, if they have it saved.
            name="first_name"
            className={clsx(
              'text-center font-bold rounded-full border-2 px-56 py-24 text-l',
              limitError || emptyInput || invalidInput ? 'border-red-600' : 'border-black'
            )}
            type="text"
            placeholder="Enter Username"
            value={username}
            onChange={(e) => inputUsername(e)}
            onKeyDown={(e) => handleKeyDown(e)}
          />
          <span className="mt-8 text-red-600 text-xs mr-80">{errorMessage()}</span>
          <p className="mt-24 mb-8 text-lg font-black w-full flex justify-center items-center text-[#20145F]">
            How do you feel?
          </p>
          <FeelingMeter sentiment={sentiment} setSentiment={setSentiment} />
          {!sentiment && wasSubmissionTriggered && (
            <span className="mt-8 text-red-600 text-xs mr-80">*Selection is required</span>
          )}

          <button
            className="btn btn-primary btn-lg w-full font-bold text-white mt-16"
            onClick={submit}
            disabled={isSubmitButtonDisabled}
          >
            Let&apos;s Play
            {loading && <FontAwesomeIcon icon={faSpinner} className="ml-8" spin />}
          </button>
        </div>
      </div>
    </div>
  );
}
